import classNames from 'classnames';

export default class BulletsAnimation {
	/**
	 * Insert animation and highlight class to the element specified by rootSelectorOrElement
	 * @param {Element|String} rootSelectorOrElement
	 * @param {Array|String} additionalClasses
	 * @param {string} removeHighlightClickChildSelector
	 * @param {Number} tickMs
	 * @param {Int} totalSize 
	 * @param {Int} visibleSize 
	 * @param {Int} activeSize 
	 * @param {Bool} symmetrical 
	 * @param {string} id
	 * @param {string} activeBulletClass
	 */
	constructor(rootSelectorOrElement, additionalClasses = '', tickMs = 70, totalSize = 15, visibleSize = 6, activeSize = 4, symmetrical = true, id = '', activeBulletClass = 'dpit-on') {

		this.rootSelectorOrElement = rootSelectorOrElement;
		this.bemRoot = 'dpit-anim-rb';
		this.additionalClasses = classNames([additionalClasses, 'dpit-anim-rb']);
		this.inactiveClass = BulletsAnimation.getModifiedClass(this.bemRoot, 'off');
		this.activeClass = activeBulletClass;
		this.id = id;
		this.visibleSize = visibleSize;
		this.totalSize = totalSize;
		this.tickMs = tickMs;
		this.activeSize = activeSize;
		this.activeHalfSize = Math.floor(activeSize / 2);
		this.symmetrical = symmetrical;
	}

	getRootElement() {
		return this.rootSelectorOrElement instanceof HTMLElement
			? this.rootSelectorOrElement
			: document.querySelector(this.rootSelectorOrElement);
	}

	static getModifiedClass(bemRoot, modifier) {
		let res = '';
		if (bemRoot) res = bemRoot + '--';
		res += modifier;
		return res;
	}

	start(backward = false, loops = -1, loopsPassedCb = null) {
		this.backward = backward;
		this.rootElement = this.getRootElement();
		if (this.rootElement) {
			this.rootElement.focus({ preventScroll: true });
			this.isRunning = true;
			if (!this.bulletsLayer) {
				this.createBullets(this.visibleSize, this.rootElement);
			} else {
				this.bulletsLayer.classList.remove(this.inactiveClass);
			}

			if (backward) this.startReloadingSpinnerBack(loops, loopsPassedCb);
			else this.startReloadingSpinner(loops, loopsPassedCb);
		}
	}

	getLoopTimeMs() {
		return this.totalSize * this.tickMs;
	}

	createBullets(count, rootElement) {

		this.bulletsLayer = document.createElement('div');
		this.bulletsLayer.className = classNames([this.bemRoot, this.additionalClasses]);
		this.bulletsLayer.setAttribute('aria-hidden', 'true');
		this.bulletsLayer.id = this.id;
		this.bulletsLayer.style.opacity = 0;
		this.bulletsLayer.style.display = 'block';
		this.bulletElements = [];
		const containerEl = document.createElement('div');
		containerEl.classList.add(this.bemRoot + '__wrap');
		for (let i = 0; i < count; i++) {
			const bulletEl = document.createElement('div');
			this.bulletElements.push(bulletEl);
			containerEl.appendChild(bulletEl);
		}
		this.bulletsLayer.appendChild(containerEl);
		this.bulletsLayer.style.opacity = 1;
		rootElement.appendChild(this.bulletsLayer);
	}



	activateBullet(activeIdx) {
		this.bulletElements.forEach((element, idx) => {
			let className = '';
			const distanceFromActive = activeIdx - idx;
			if (distanceFromActive === 0) {
				className = this.activeClass;
			} else {
				const distanceFromActiveAbs = Math.abs(distanceFromActive);
				if (this.symmetrical) {
					if (distanceFromActiveAbs <= this.activeHalfSize) {
						className = this.activeClass + '--' + distanceFromActiveAbs;
					}
				}
				else if (distanceFromActiveAbs < this.activeSize) {
					if (distanceFromActive > 0 && !this.backward) {
						className = this.activeClass + '--' + distanceFromActiveAbs;
					}
					else if (distanceFromActive < 0 && this.backward) {
						className = this.activeClass + '--' + distanceFromActiveAbs;
					}
				}
			}
			element.className = className;
		});
	}

	stopAfterLoopEnd(loopEndCallback = null) {
		this.finishAfterLoopEnd = true;
		this.finishAfterLoopEndCallback = loopEndCallback;
	}

	stop() {
		this.bulletsLayer?.classList.add(this.inactiveClass);
		this.clearBulletActiveStates();
		this.stopReloadSpinner();
		this.rootElement?.blur();
	}

	clearBulletActiveStates() {
		this.bulletElements?.forEach(element => {
			element.className = '';
		});
	}

	stopReloadSpinner() {
		if (this.spinnerInterval) clearInterval(this.spinnerInterval);
		this.spinnerInterval = null;
		this.isRunning = false;
	}

	startReloadingSpinner(loops, loopsPassedCb) {
		let idx = this.symmetrical ? - this.activeHalfSize : 0;
		if (this.spinnerInterval) clearInterval(this.spinnerInterval);
		if (this.bulletHideTimeout) clearTimeout(this.bulletHideTimeout);
		this.spinnerInterval = setInterval(() => {
			this.activateBullet(idx, idx - 1);
			idx++;
			if (idx > this.totalSize) {
				idx = this.symmetrical ? - this.activeHalfSize : 0;
				this.checkAfterLoopEnd();
				if (loops > 0) {
					loops = this.finishLoop(loops, loopsPassedCb);
				}
			}

		}, this.tickMs);
	}

	startReloadingSpinnerBack(loops, loopsPassedCb) {
		if (this.spinnerInterval) clearInterval(this.spinnerInterval);
		if (this.bulletHideTimeout) clearTimeout(this.bulletHideTimeout);
		let idx = this.symmetrical ? this.visibleSize + this.activeHalfSize : this.visibleSize;
		this.spinnerInterval = setInterval(() => {
			this.activateBullet(idx);
			idx--;
			if (idx < this.visibleSize - this.totalSize) {
				idx = this.symmetrical ? this.visibleSize + this.activeHalfSize : this.visibleSize;
				this.checkAfterLoopEnd();
				if (loops > 0) {
					loops = this.finishLoop(loops, loopsPassedCb);
				}

			}

		}, this.tickMs);
	}

	checkAfterLoopEnd() {
		if (this.finishAfterLoopEnd) {
			this.finishAfterLoopEnd = false;
			this.stop();
			if (this.finishAfterLoopEndCallback instanceof Function) this.finishAfterLoopEndCallback();
			this.finishAfterLoopEndCallback = null;
		}
	}

	finishLoop(loops, loopsPassedCb) {
		if (loops > 0) {
			loops--;
			if (loops === 0) {
				this.stop();
				if (loopsPassedCb && loopsPassedCb instanceof Function) loopsPassedCb();
			}
		}
		return loops;
	}

}
